import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, AlertCircle, Loader, Printer } from "lucide-react";
import Api from "../Api/Api.json";

export default function PaymentResult() {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentID = params.get("paymentID");
    const statusParam = params.get("status");
    const signature = params.get("signature");

    // Get shipping info from localStorage
    const storedShippingInfo = localStorage.getItem('shipping_info');
    let shippingData = null;
    if (storedShippingInfo) {
      shippingData = JSON.parse(storedShippingInfo);
      setShippingInfo(shippingData);
    }

    if (!paymentID || !statusParam || !signature) {
      setStatus("failure");
      setIsLoading(false);
      return;
    }

    const body = {
      task: "executePaymentBackendUpskill",
      paymentID: paymentID.toString(),
      status: statusParam.toString(),
      signature: signature.toString(),
    };

    const headers = { "Content-Type": "application/json" };
    axios
      .post(Api.BKASH_API, body, { headers })
      .then((response) => {
        if (response.data.status === "success") {
          setStatus("success");
          setPaymentDetails({
            paymentID: paymentID,
            transactionID: response.data.transactionID || paymentID,
            amount: response.data.amount || shippingData?.amount,
            date: new Date().toLocaleString(),
          });
        } else {
          setStatus("failure");
        }
      })
      .catch((err) => {
        console.error("Error executing payment:", err);
        setStatus("failure");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handlePrintInvoice = () => {
    if (paymentDetails && shippingInfo) {
      const printContent = `
        <html>
          <head>
            <title>Invoice - Payment Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .invoice-header { text-align: center; margin-bottom: 30px; }
              .invoice-header h1 { color: #2563eb; margin: 0; }
              .invoice-section { margin-bottom: 20px; }
              .invoice-section h2 { border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; }
              .info-row { display: flex; justify-content: space-between; margin: 10px 0; }
              .info-label { font-weight: bold; color: #4b5563; }
              .info-value { color: #111827; }
              .total-box { background: #eff6ff; padding: 15px; border-radius: 8px; margin-top: 20px; }
              .footer { margin-top: 40px; text-align: center; color: #6b7280; font-size: 12px; }
              @media print {
                body { padding: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="invoice-header">
              <h1>Semion Health - Payment Invoice</h1>
              <p>Invoice Receipt</p>
            </div>
            
            <div class="invoice-section">
              <h2>Payment Information</h2>
              <div class="info-row">
                <span class="info-label">Payment ID:</span>
                <span class="info-value">${paymentDetails.paymentID}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Transaction ID:</span>
                <span class="info-value">${paymentDetails.transactionID}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Amount:</span>
                <span class="info-value">৳${paymentDetails.amount?.toLocaleString()}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Date:</span>
                <span class="info-value">${paymentDetails.date}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">bKash</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value" style="color: #10b981; font-weight: bold;">Success</span>
              </div>
            </div>

            <div class="invoice-section">
              <h2>Shipping Information</h2>
              <div class="info-row">
                <span class="info-label">Shipping Address:</span>
                <span class="info-value" style="text-align: right; max-width: 60%;">${shippingInfo.address}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone Number:</span>
                <span class="info-value">${shippingInfo.phone}</span>
              </div>
            </div>

            <div class="total-box">
              <div class="info-row">
                <span class="info-label" style="font-size: 18px;">Total Amount:</span>
                <span class="info-value" style="font-size: 20px; font-weight: bold; color: #2563eb;">৳${paymentDetails.amount?.toLocaleString()}</span>
              </div>
            </div>

            <div class="footer">
              <p>Thank you for your purchase!</p>
              <p>Semion Health - Your Health Companion</p>
              <p>This is an automated invoice generated on ${new Date().toLocaleString()}</p>
            </div>
          </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Processing payment result...</p>
        </div>
      </div>
    );
  }

  const isSuccess = status === "success";
  const isFailure = status === "failure";
  const isCancel = status === "cancel";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-4 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-6 flex justify-center">
          {isSuccess && (
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          )}
          {isFailure && (
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
          )}
          {isCancel && (
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-12 h-12 text-yellow-600" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <h2 className="text-3xl font-bold mb-2">
          {isSuccess && <span className="text-green-600">Payment Successful!</span>}
          {isFailure && <span className="text-red-600">Payment Failed</span>}
          {isCancel && <span className="text-yellow-600">Payment Cancelled</span>}
        </h2>

        <p className="text-gray-600 mb-8">
          {isSuccess && "Your payment has been processed successfully."}
          {isFailure && "There was an issue processing your payment. Please try again."}
          {isCancel && "Your payment was cancelled. You can try again when ready."}
        </p>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isSuccess && paymentDetails && shippingInfo && (
            <button
              onClick={handlePrintInvoice}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Printer className="w-5 h-5" />
              <span>Print Invoice</span>
            </button>
          )}
          {isSuccess && (
            <button
              onClick={() => navigate("/payment")}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Make Another Payment
            </button>
          )}
          {(isFailure || isCancel) && (
            <button
              onClick={() => navigate("/payment")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Try Again
            </button>
          )}
          <button
            onClick={() => navigate("/landing")}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Go to Home
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Powered by bKash • Secure Payment Gateway
          </p>
        </div>
      </div>
    </div>
  );
}

